import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { useRef, useState, useEffect, useCallback } from "react";
import "lazysizes";
import "lazysizes/plugins/parent-fit/ls.parent-fit";
import { motion } from "framer-motion";
import { Box } from "@theme-ui/components";
import { Modal, Gallery, ImgixAspectRatio, MagnifyIncrease, MagnifyDecrease } from "../../index";
import { isTouchDevice, getWindowDimensions } from "../../utils";
import { zoomAllowedKeys, calculateImageMaxDimensions, updateImageObjectPositionCss, getMaxCursorPositionForImage, shouldCenterImageWithinContainer, getNewAxisPositionForKeyPressed } from "../../utils/zoom";
const ZOOM_FACTOR = 3;
const IMG_PADDING_X = 80;
const IMG_PADDING_Y = 90;
const IMG_MAX_WIDTH = 1280;
const IMG_MAX_HEIGHT = 720;
const AFTER_PAGE_CHANGE_DELAY_MS = 400;
const EXTRA_LARGE_BREAKPOINT_PX = 2000;
const ZoomButton = ({ onClick, style = {}, type, disabled = false }) => (_jsxs("button", { disabled: disabled, "aria-label": `Zoom ${type === "increase" ? "In" : "Out"}`, onClick: onClick, sx: Object.assign({ variant: "buttons.hollowLight", backgroundColor: disabled ? "grey.2" : "grey.0", "&:hover": {
            backgroundColor: disabled ? "grey.2" : "grey.0"
        } }, style), children: [type === "increase" && _jsx(MagnifyIncrease, { color: "white" }), type === "decrease" && _jsx(MagnifyDecrease, { color: "white" })] }));
export const ZoomGallery = ({ images, currentPage = 0, close, show }) => {
    const [lazyLoadClassName, setLazyLoadClassName] = useState("lazyload");
    const [zoomMode, setZoomMode] = useState({
        enabled: false,
        centered: false,
        keyboard: false
    });
    const vars = useRef(Object.assign({ maxPosition: { x: 0, y: 0 }, touchMoved: false, imageRef: null, shouldCenterZoomImage: false, zoomPosition: {
            x: 0,
            y: 0
        } }, getWindowDimensions()));
    const { current: v } = vars;
    const imageRef = useCallback((node) => {
        if (node)
            v.imageRef = node;
    }, []);
    const resetZoomMode = () => setZoomMode({ enabled: false, centered: false, keyboard: false });
    const handleAfterPageChange = () => {
        // small delay added to lazyload images so gallery swipe is smooth on mobile devices. Otherwise swipe + lazyload effect is a bit clunky.
        setTimeout(() => setLazyLoadClassName("lazyload"), AFTER_PAGE_CHANGE_DELAY_MS);
    };
    const handleBeforePageChange = () => {
        setLazyLoadClassName("");
        resetZoomMode();
    };
    const handleTouchStart = (e) => {
        v.touchMoved = false;
        const image = e.target;
        const bcr = image.getBoundingClientRect();
        const posX = e.touches[0].clientX - bcr.left;
        const posY = e.touches[0].clientY - bcr.top;
        v.zoomPosition.x = posX * (ZOOM_FACTOR - 1);
        v.zoomPosition.y = posY * (ZOOM_FACTOR - 1);
    };
    const handleTouchEnd = (e) => {
        // only toggle zoom mode if user is not dragging the touch screen
        if (v.touchMoved !== true) {
            if (!zoomMode.enabled) {
                v.shouldCenterZoomImage = shouldCenterImageWithinContainer(e.target, ZOOM_FACTOR);
            }
            setZoomMode({
                enabled: !zoomMode.enabled,
                centered: v.shouldCenterZoomImage,
                keyboard: false
            });
        }
    };
    const handleMouseMove = (e) => {
        var _a, _b;
        e.preventDefault();
        if (!zoomMode.enabled || isTouchDevice())
            return;
        const image = e.target;
        const posX = (_a = e.nativeEvent) === null || _a === void 0 ? void 0 : _a.offsetX;
        const posY = (_b = e.nativeEvent) === null || _b === void 0 ? void 0 : _b.offsetY;
        // Some images have smaller dimensions than the container, so we need to check if mouse zoom can be moved to a certain position
        // so it doesn't create empty spaces on the X/Y axis. If the cursor position is smaller than the max allowed position, then we let
        // moving the image to that new position, otherwise we don't update the image coordinates
        v.zoomPosition.x = posX <= v.maxPosition.x ? posX : v.zoomPosition.x;
        v.zoomPosition.y = posY <= v.maxPosition.y ? posY : v.zoomPosition.y;
        updateImageObjectPositionCss(image, v.zoomPosition, ZOOM_FACTOR);
    };
    const handleKeyDown = (e) => {
        if (e.key !== "Enter" && !zoomMode.enabled)
            return;
        if (zoomAllowedKeys.indexOf(e.key) === -1) {
            return;
        }
        const image = e.target;
        const imageContainer = image.parentElement;
        if (!zoomMode.enabled) {
            v.shouldCenterZoomImage = shouldCenterImageWithinContainer(image, ZOOM_FACTOR);
            v.zoomPosition.x = (imageContainer.offsetWidth + IMG_PADDING_X) / 2;
            v.zoomPosition.y = (imageContainer.offsetHeight + IMG_PADDING_Y) / 2;
        }
        else {
            v.zoomPosition = getNewAxisPositionForKeyPressed(e.key, v.zoomPosition, v.maxPosition);
            updateImageObjectPositionCss(image, v.zoomPosition, ZOOM_FACTOR);
        }
        if (e.key === "Enter") {
            setZoomMode({
                enabled: !zoomMode.enabled,
                keyboard: true,
                centered: v.shouldCenterZoomImage
            });
        }
    };
    const handleZoomInClick = (e) => {
        let centered = false;
        if (v.imageRef) {
            centered = shouldCenterImageWithinContainer(v.imageRef, ZOOM_FACTOR);
            const bcr = v.imageRef.getBoundingClientRect();
            if (isTouchDevice()) {
                const posX = v.windowWidth / 2 - bcr.left;
                v.zoomPosition.x = posX * (ZOOM_FACTOR - 1);
                v.zoomPosition.y = 0;
            }
            else {
                v.zoomPosition.x =
                    bcr.width / 2 + (v.windowWidth > 864 ? IMG_PADDING_X : 0);
                v.zoomPosition.y =
                    bcr.height / 2 + (v.windowWidth > 864 ? IMG_PADDING_Y : 0);
            }
        }
        setZoomMode({
            enabled: true,
            centered,
            keyboard: false
        });
    };
    const handleMouseDown = (e, imageData) => {
        var _a, _b;
        if (isTouchDevice())
            return;
        e.preventDefault();
        if (!zoomMode.enabled) {
            const image = e.target;
            const { imageWidth } = calculateImageMaxDimensions(imageData, IMG_MAX_HEIGHT);
            const zoomedImageWillTakeFullScreen = imageWidth >= IMG_MAX_WIDTH;
            v.zoomPosition.x = zoomedImageWillTakeFullScreen
                ? e.clientX
                : ((_a = e.nativeEvent) === null || _a === void 0 ? void 0 : _a.offsetX) + IMG_PADDING_X;
            v.zoomPosition.y = zoomedImageWillTakeFullScreen
                ? e.clientY
                : ((_b = e.nativeEvent) === null || _b === void 0 ? void 0 : _b.offsetY) + IMG_PADDING_Y;
            v.shouldCenterZoomImage = shouldCenterImageWithinContainer(image, ZOOM_FACTOR);
        }
        setZoomMode({
            enabled: !zoomMode.enabled,
            centered: v.shouldCenterZoomImage,
            keyboard: false
        });
    };
    const handleTouchMove = (e) => {
        //prevent behaviour for multi-touch
        if (e.touches.length > 1) {
            return e.preventDefault();
        }
        v.touchMoved = true;
    };
    useEffect(() => {
        const handleWindowResize = () => {
            const dimensions = getWindowDimensions();
            v.windowWidth = dimensions.windowWidth;
            v.windowHeight = dimensions.windowHeight;
        };
        window.addEventListener("resize", handleWindowResize);
        return () => window.removeEventListener("resize", handleWindowResize);
    }, []);
    useEffect(() => {
        var _a;
        // Every time the zoom changes its state, we update the zoom position (for desktop) and scrolling (for mobile)
        const img = v.imageRef;
        if (!img)
            return;
        if (!zoomMode.enabled) {
            v.zoomPosition = { x: 0, y: 0 };
            updateImageObjectPositionCss(img, v.zoomPosition, ZOOM_FACTOR);
            if (zoomMode.keyboard) {
                img.focus();
            }
        }
        else {
            if (isTouchDevice()) {
                (_a = img.parentElement) === null || _a === void 0 ? void 0 : _a.scrollTo(v.zoomPosition.x, v.zoomPosition.y);
            }
            else {
                img.onload = () => {
                    v.maxPosition = getMaxCursorPositionForImage(img, ZOOM_FACTOR);
                    v.zoomPosition.x =
                        v.zoomPosition.x <= v.maxPosition.x
                            ? v.zoomPosition.x
                            : v.maxPosition.x;
                    v.zoomPosition.y =
                        v.zoomPosition.y <= v.maxPosition.y
                            ? v.zoomPosition.y
                            : v.maxPosition.y;
                    updateImageObjectPositionCss(img, v.zoomPosition, ZOOM_FACTOR);
                    if (zoomMode.keyboard) {
                        img.focus();
                    }
                };
            }
        }
    }, [zoomMode]);
    useEffect(() => {
        if (!show)
            resetZoomMode();
    }, [show]);
    if (!images.length) {
        return null;
    }
    return (_jsx(Modal, { show: show, backgroundColor: "grey.4", height: ["100%", "100%", "100%", "100%"], maxHeight: ["100%", "100%", "100%", "100%"], width: ["100%", "100%"], innerHeight: ["100%", "100%", "100%", "100%"], paddingX: 0, paddingY: 0, close: close, children: _jsxs(Box, { sx: {
                height: "100%"
            }, children: [_jsxs(Box, { margin: [`${IMG_PADDING_Y}px 0`], sx: {
                        position: "absolute",
                        right: ["16px", "16px", "80px"],
                        "& button": {
                            height: [40, 40, 24],
                            width: [40, 40, 24],
                            zIndex: 10,
                            padding: 2,
                            borderRadius: 0
                        }
                    }, children: [_jsx(ZoomButton, { type: "increase", onClick: e => handleZoomInClick(e), disabled: zoomMode.enabled }), _jsx(ZoomButton, { type: "decrease", disabled: !zoomMode.enabled, onClick: () => resetZoomMode(), style: {
                                mt: 3
                            } })] }), _jsx(Gallery, { style: {
                        height: "100%",
                        pb: ["100%", "100%", IMG_MAX_HEIGHT + IMG_PADDING_Y * 2]
                    }, id: "zoom-gallery", ui_arrows: "overlay-bottom", pageIndicatorMode: "bottom", dragDisabled: zoomMode.enabled, beforePageChange: handleBeforePageChange, afterPageChange: handleAfterPageChange, currentPage: currentPage, children: images.map((image, idx) => {
                        const imageUrl = image.url.split("?")[0];
                        const { imageWidth, imageHeight } = calculateImageMaxDimensions(image, IMG_MAX_HEIGHT);
                        const shouldAdjustHeight = imageHeight > imageWidth && imageWidth < v.windowWidth;
                        const percentageDimension = zoomMode.enabled
                            ? `${ZOOM_FACTOR * 100}%`
                            : "100%";
                        const width = shouldAdjustHeight ? "auto" : percentageDimension;
                        const height = shouldAdjustHeight ? percentageDimension : "auto";
                        const lowQualityImage = `${imageUrl}?w=${imageWidth / 20}&h=${imageHeight / 20}&blur=30&auto=format&fit=crop`;
                        const imgStyle = {
                            position: (zoomMode.enabled ? "absolute" : "relative"),
                            cursor: zoomMode.enabled ? "zoom-out" : "zoom-in",
                            margin: "0 auto",
                            outline: 0
                        };
                        return (_jsx(Box, { margin: zoomMode.enabled
                                ? "0 auto"
                                : ["0 auto", "0 auto", `${IMG_PADDING_Y}px auto`], sx: {
                                position: "relative",
                                display: zoomMode.enabled ? "block" : "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                overflow: isTouchDevice() && zoomMode.enabled ? "scroll" : "hidden",
                                maxHeight: zoomMode.enabled
                                    ? "100%"
                                    : ["100%", "100%", `calc(100% - ${2 * IMG_PADDING_Y}px)`],
                                maxWidth: zoomMode.enabled
                                    ? "100%"
                                    : [
                                        "100%",
                                        "100%",
                                        `calc(${IMG_MAX_WIDTH}px - ${2 * IMG_PADDING_X}px)`
                                    ],
                                height: "100%",
                                width: [
                                    "100%",
                                    "100%",
                                    imageWidth >= IMG_MAX_WIDTH ||
                                        v.windowHeight >= v.windowWidth
                                        ? "100%"
                                        : zoomMode.enabled
                                            ? imageWidth + 2 * IMG_PADDING_Y
                                            : imageWidth
                                ],
                                [`@media screen and (min-width: ${EXTRA_LARGE_BREAKPOINT_PX}px)`]: {
                                    width: v.windowHeight >= v.windowWidth
                                        ? "100%"
                                        : image.aspectRatio === ImgixAspectRatio.ar16x9 ||
                                            image.aspectRatio === ImgixAspectRatio.ar21x9
                                            ? "100%"
                                            : zoomMode.enabled
                                                ? `calc(${(imageWidth / imageHeight) * 50}% + ${IMG_PADDING_X}px)`
                                                : `${(imageWidth / imageHeight) * 50}%`,
                                    maxWidth: "100%"
                                }
                            }, children: _jsx(motion.img, { className: lazyLoadClassName, tabIndex: 0, sx: zoomMode.centered
                                    ? Object.assign(Object.assign({}, imgStyle), { position: "absolute", top: "50%", transform: "translateY(-50%)" }) : imgStyle, ref: imageRef, src: lowQualityImage, "data-srcset": `${imageUrl}?auto=format&w=${imageWidth * 3}&h=${imageHeight * 3}&fit=crop 1x,
                  ${imageUrl}?auto=format&w=${imageWidth * 4}&h=${imageHeight * 4}&fit=crop 2x,
                  ${imageUrl}?auto=format&w=${imageWidth * 5}&h=${imageHeight * 5}&fit=crop 3x,
                  `, initial: { opacity: zoomMode.enabled ? 0 : 1 }, animate: { opacity: 1 }, onKeyDown: handleKeyDown, onTouchMove: handleTouchMove, onMouseMove: handleMouseMove, onMouseDown: e => handleMouseDown(e, image), onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, alt: image.title, width: width, height: height }) }, idx));
                    }) })] }) }));
};
//# sourceMappingURL=ZoomGallery.js.map